import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, RotateCcw, RotateCw, Video, Ban, X, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { HistoryItem } from '@/hooks/useHistory'
import { useCancelGeneration } from '@/hooks/useHistory'
import { localizeName } from '@/lib/i18n-content'
import { toast } from 'sonner'

function isPortraitRatio(ratio: string): boolean {
  const [w, h] = ratio.split(':').map(Number)
  return h > w
}

interface QueuePlaceholderCardProps {
  item: HistoryItem
  onReuse?: () => void
  /** Failed/cancelled: send the same generation again. */
  onRetry?: () => void
  retrying?: boolean
  /** Failed/cancelled: delete the record (the caller confirms). */
  onDelete?: () => void
}

export function QueuePlaceholderCard({
  item,
  onReuse,
  onRetry,
  retrying = false,
  onDelete,
}: QueuePlaceholderCardProps) {
  const { t } = useTranslation()
  const cancelMutation = useCancelGeneration()
  const { status, taskPhase } = item
  const finished = status === 'failed' || status === 'cancelled'
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)

  const canCancel = status === 'processing' && taskPhase === 'queued'

  const handleCancelConfirm = async () => {
    try {
      await cancelMutation.mutateAsync(item.id)
      setCancelDialogOpen(false)
      toast.success(t('queue.cancelSuccess'))
    } catch (error) {
      toast.error(t('queue.cancelFailed'), {
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  const statusChip = {
    processing: {
      className: 'bg-primary/10 text-primary',
      icon: <Loader2 className="w-3 h-3 animate-spin" />,
      text: taskPhase === 'queued' ? t('queue.waiting') : t('queue.generating'),
    },
    failed: {
      className: 'bg-red-100 text-red-700',
      icon: <AlertCircle className="w-3 h-3" />,
      text: t('queue.failed'),
    },
    cancelled: {
      className: 'bg-slate-100 text-slate-500',
      icon: <Ban className="w-3 h-3" />,
      text: t('queue.cancelled'),
    },
  }

  const currentStatus = statusChip[status as keyof typeof statusChip]
  if (!currentStatus) return null

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-slate-800 line-clamp-2 text-sm">
                {item.prompt}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {item.template && (
                  <span className="inline-flex items-center text-xs bg-primary/10 text-primary px-2 py-1 rounded-md">
                    {localizeName(item.template)}
                  </span>
                )}
                <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                  {item.ratio}
                </span>
                {item.resolution && (
                  <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                    {item.resolution}
                  </span>
                )}
                {item.duration && (
                  <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                    {t('common.seconds', { count: item.duration })}
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${currentStatus.className}`}>
                  {currentStatus.icon}
                  {currentStatus.text}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {canCancel && (
                <button
                  onClick={() => setCancelDialogOpen(true)}
                  className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                  title={t('queue.cancelGeneration')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {finished && onRetry && (
                <Button
                  size="sm"
                  onClick={onRetry}
                  disabled={retrying}
                  className="h-7 text-xs"
                >
                  {retrying ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <RotateCw className="w-3 h-3 mr-1" />
                  )}
                  {t('common.retry')}
                </Button>
              )}
              {finished && onReuse && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReuse}
                  className="h-7 text-xs"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  {t('history.reuse')}
                </Button>
              )}
              {finished && onDelete && (
                <button
                  onClick={onDelete}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-slate-50 transition-colors"
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* The raw cause (errorMessage) is for admins: the admin generation detail shows it. */}
          {status === 'failed' && (
            <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-sm text-red-700">{t('queue.failedDescription')}</p>
            </div>
          )}
        </div>

        <div className="p-4">
          <div className={isPortraitRatio(item.ratio) ? 'max-w-xs' : 'max-w-lg'}>
            <div
              className={`relative rounded-lg overflow-hidden ${
                status === 'failed' ? 'bg-red-50' : status === 'cancelled' ? 'bg-slate-50' : 'bg-slate-100'
              }`}
              style={{ aspectRatio: item.ratio.replace(':', '/') }}
            >
              {status === 'processing' && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer" />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <Video className={`w-12 h-12 ${
                  status === 'failed' ? 'text-red-200' : 'text-slate-300'
                }`} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('queue.cancelDialogTitle')}</DialogTitle>
            <DialogDescription className="mt-2">
              {t('queue.cancelDialogDescription1')}
              <br />
              {t('queue.cancelDialogDescription2')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelMutation.isPending}
            >
              {t('queue.goBack')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelConfirm}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('queue.cancelling')}
                </>
              ) : (
                t('queue.confirmCancel')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
